import React from 'react';
import type {
  PetKind,
  PetIllustrationVariant,
  IllustrationPalette,
} from '../types';

const TOKENS = {
  ink: '#0f2a4a',
  inkSoft: '#1f3c5f',
  paper: '#f3ead8',
  paperWarm: '#fcf6e4',
  paperDeep: '#ebdcc0',
  terracotta: '#c25a3a',
  ochre: '#c68c3b',
  rust: '#a14a2b',
  sage: '#7a8b5f',
} as const;

const DEFAULT_PALETTE: IllustrationPalette = {
  bg: TOKENS.ochre,
  body: TOKENS.ink,
  eyes: TOKENS.paper,
  mouth: TOKENS.paper,
};

interface DogProps {
  palette?: Partial<IllustrationPalette>;
  variant?: PetIllustrationVariant;
}

const Dog: React.FC<DogProps> = ({ palette, variant }) => {
  const { bg, body, eyes, mouth, accent } = { ...DEFAULT_PALETTE, ...palette };
  return (
    <svg
      viewBox="0 0 120 150"
      preserveAspectRatio="xMidYMid slice"
      width="100%"
      height="100%"
    >
      <rect width="120" height="150" fill={bg} />
      <g fill={body}>
        <ellipse
          cx="60"
          cy={variant === 'senior' ? 96 : 88}
          rx={variant === 'senior' ? 38 : 34}
          ry={variant === 'senior' ? 42 : 36}
        />
        {variant === 'floppy' ? (
          <>
            <ellipse cx="30" cy="74" rx="13" ry="22" transform="rotate(-22 30 74)" />
            <ellipse cx="90" cy="74" rx="13" ry="22" transform="rotate(22 90 74)" />
          </>
        ) : (
          <>
            <ellipse cx="34" cy="58" rx="12" ry="20" transform="rotate(-18 34 58)" />
            <ellipse cx="86" cy="58" rx="12" ry="20" transform="rotate(18 86 58)" />
          </>
        )}
      </g>
      <g fill={eyes}>
        <circle cx="48" cy={variant === 'senior' ? 92 : 84} r="3" />
        <circle cx="72" cy={variant === 'senior' ? 92 : 84} r="3" />
        <ellipse
          cx="60"
          cy={variant === 'senior' ? 108 : 100}
          rx={variant === 'senior' ? 6 : 5}
          ry={variant === 'senior' ? 4 : 3}
        />
      </g>
      <path
        d={
          variant === 'senior'
            ? 'M54 115 Q60 122 66 115'
            : 'M54 108 Q60 114 66 108'
        }
        stroke={mouth}
        strokeWidth="1.5"
        fill="none"
      />
      {variant === 'senior' && accent && (
        <circle cx="52" cy="80" r="5" fill={accent} opacity="0.6" />
      )}
    </svg>
  );
};

interface CatProps {
  palette?: Partial<IllustrationPalette>;
  variant?: PetIllustrationVariant;
}

const Cat: React.FC<CatProps> = ({ palette, variant }) => {
  const { bg, body, eyes, mouth } = {
    ...DEFAULT_PALETTE,
    bg: TOKENS.sage,
    ...palette,
  };
  return (
    <svg
      viewBox="0 0 120 150"
      preserveAspectRatio="xMidYMid slice"
      width="100%"
      height="100%"
    >
      <rect width="120" height="150" fill={bg} />
      <g fill={body}>
        <ellipse cx="60" cy="90" rx={32} ry={34} />
        <path d="M34 64 L26 44 L46 56 Z" />
        <path d="M86 64 L94 44 L74 56 Z" />
      </g>
      <g fill={eyes}>
        <ellipse cx="48" cy="86" rx="3" ry="4" />
        <ellipse cx="72" cy="86" rx="3" ry="4" />
        {variant === 'smile' ? (
          <path d="M56 102 Q60 106 64 102" stroke={mouth} strokeWidth="1.6" fill="none" />
        ) : (
          <path d="M54 100 L60 106 L66 100" stroke={mouth} strokeWidth="2" fill="none" />
        )}
      </g>
      {variant !== 'smile' && (
        <g stroke={mouth} strokeWidth="1" fill="none" opacity="0.55">
          <path d="M42 94 L22 90 M42 98 L22 98 M80 94 L100 90 M80 98 L100 98" />
        </g>
      )}
    </svg>
  );
};

interface PetIllustrationProps {
  kind?: PetKind;
  variant?: PetIllustrationVariant;
  palette?: Partial<IllustrationPalette>;
  className?: string;
  style?: React.CSSProperties;
}

const PetIllustration: React.FC<PetIllustrationProps> & { tokens: typeof TOKENS } = ({
  kind = 'dog',
  variant = 'default',
  palette,
  className,
  style,
}) => (
  <div className={className} style={{ width: '100%', height: '100%', ...style }}>
    {kind === 'cat' ? (
      <Cat palette={palette} variant={variant} />
    ) : (
      <Dog palette={palette} variant={variant} />
    )}
  </div>
);

PetIllustration.tokens = TOKENS;

export default PetIllustration;
